import React, { useEffect, useMemo, useRef, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { printPackageLabel } from "../lib/packageLabel";
import OpsHistoryPanel from "../components/admin/delivery/OpsHistoryPanel";
import LabelQrCell, { useLabelQrs } from "../components/deliveries/LabelQrCell";
import DeliveryMlDataPanel from "../components/admin/delivery/DeliveryMlDataPanel";

type DeliveryRaw = {
  id?: number;
  driver_id?: number | null;
  seller_id?: number | null;
  store_id?: number | null;
  account_id?: number | null;
  account_name?: string | null;
  account_nombre?: string | null;
  account_nombre_fantasia?: string | null;
  accountName?: string | null;
  account?: string | null;
  id_venta_ml?: string | null;
  tracking?: number | string | null;
  cadete?: string | null;
  nombre_fantasia?: string | null;
  nombre_destinatario?: string | null;
  telefono_destinatario?: string | null;
  email_destinatario?: string | null;
  comentario_destino?: string | null;
  direccion?: string | null;
  codigo_postal?: string | null;
  localidad?: string | null;
  provincia?: string | null;
  origen?: string | null;
  tipo_envio?: string | null;
  is_turbo?: boolean | null;
  status?: string | null;
  status_ml?: string | null;
  updated_by?: string | null;
  fecha_wynflex?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  total?: number | string | null;
  precio_cliente?: number | string | null;
  precio_chofer?: number | string | null;
  assigned_driver_id?: number | string | null;
  assigned_driver_user_id?: number | string | null;
  assigned_driver_username?: string | null;
  assigned_driver_first_name?: string | null;
  assigned_driver_last_name?: string | null;
  assigned_driver_name?: string | null;
  assigned_driver_vehicle?: string | null;
  assigned_driver_plate?: string | null;
  fecha_venta?: string | null;
  sale_date?: string | null;
  date_created?: string | null;
  order_date?: string | null;
  fecha_colecta?: string | null;
  collection_date?: string | null;
};

type DeliveryStatusRaw = {
  delivery_id?: number;
  status?: string | null;
  status_ml?: string | null;
  updated_by?: string | null;
  fecha_wynflex?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type DeliveryFinancialRaw = {
  delivery_id?: number;
  total?: number | string | null;
  precio_cliente?: number | string | null;
  precio_chofer?: number | string | null;
};

type DeliveriesResponse = {
  ok?: boolean;
  error?: string;
  delivery?: DeliveryRaw[] | DeliveryRaw;
  deliveries?: DeliveryRaw[] | DeliveryRaw;
  status?: DeliveryStatusRaw[] | DeliveryStatusRaw;
  financials?: DeliveryFinancialRaw[] | DeliveryFinancialRaw;
  data?: DeliveryRaw[] | DeliveryRaw;
  result?: DeliveryRaw[] | DeliveryRaw;
};

type StoreRaw = {
  id?: number | string;
  nombre_fantasia?: string;
  nombreFantasia?: string;
  name?: string;
};

type StoresResponse = {
  ok?: boolean;
  error?: string;
  stores?: StoreRaw[];
  data?: StoreRaw[];
  result?: StoreRaw[];
};

type UserRaw = {
  id?: number | string;
  user_id?: number | string;
  driver_id?: number | string;
  driverDataId?: number | string;
  driver_data_id?: number | string;
  username?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  rol?: string | null;
  role?: string | null;
  vehicle_type?: string | null;
  vehicleType?: string | null;
  vehicle_plate?: string | null;
  vehiclePlate?: string | null;
  patente?: string | null;
  driver_data?: {
    id?: number | string;
    vehicle_type?: string | null;
    vehicleType?: string | null;
    vehicle_plate?: string | null;
    vehiclePlate?: string | null;
    patente?: string | null;
  } | null;
};

type UsersResponse = {
  ok?: boolean;
  error?: string;
  usuarios?: UserRaw[];
  users?: UserRaw[];
  data?: UserRaw[];
  result?: UserRaw[];
};

type DriverRaw = {
  id?: number | string;
  user_id?: number | string;
  vehiculo?: string | null;
  vehicle_type?: string | null;
  vehicleType?: string | null;
  patente?: string | null;
  vehicle_plate?: string | null;
  vehiclePlate?: string | null;
};

type DriversResponse = {
  ok?: boolean;
  error?: string;
  drivers?: DriverRaw[];
  driver?: DriverRaw[] | DriverRaw;
  data?: DriverRaw[];
  result?: DriverRaw[];
};

type DriverOption = {
  id: number;
  userId: number | null;
  label: string;
  displayName: string;
};

type DeliveryView = {
  id: number;
  driver_id: number | null;
  seller_id: number | null;
  store_id: number | null;
  account_id: number | null;
  account_name: string;
  store_name: string;
  id_venta_ml: string;
  tracking: string;
  origen: string;
  nombre_destinatario: string;
  direccion: string;
  codigo_postal: string;
  localidad: string;
  provincia: string;
  zona_entrega: string;
  tipo_envio: string;
  is_turbo: boolean;
  chofer: string;
  status: string;
  status_updated_by: string;
  fecha_wynflex: string;
  fecha_venta: string;
  fecha_colecta: string;
  updated_at: string;
  precio_cliente: number | null;
  precio_chofer: number | null;
  telefono_destinatario: string;
  email_destinatario: string;
  comentario_destino: string;
};

type Filters = {
  estado: string;
  origen: string;
  tipoEnvio: string;
  turbo: string;
  tracking: string;
  storeName: string;
  dateFrom: string;
  dateTo: string;
  destinatario: string;
  localidad: string;
  codigoPostal: string;
  chofer: string;
  search: string;
};

const initialFilters: Filters = {
  estado: "Todos",
  origen: "Todos",
  tipoEnvio: "Todos",
  turbo: "Todos",
  tracking: "",
  storeName: "",
  dateFrom: "",
  dateTo: "",
  destinatario: "",
  localidad: "",
  codigoPostal: "",
  chofer: "",
  search: "",
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "30px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: "1060px",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "var(--surface-alt)",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const mutedTextStyle: React.CSSProperties = {
  color: "var(--text-secondary)",
  fontSize: "12px",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
};

const labelTextStyle: React.CSSProperties = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  marginBottom: "8px",
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const selectBaseStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "46px",
  padding: "0 14px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  outline: "none",
  background: "var(--surface)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const bulkAssignBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: "12px",
  padding: "14px 16px",
  borderBottom: "1px solid var(--border)",
  flexWrap: "wrap",
  background: "var(--surface)",
};

const bottomPaginationStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "14px 16px",
  borderTop: "1px solid var(--border)",
  flexWrap: "wrap",
};

const checkboxStyle: React.CSSProperties = {
  width: "16px",
  height: "16px",
  cursor: "pointer",
  accentColor: "var(--primary)",
};

const paginationButtonStyle: React.CSSProperties = {
  minHeight: "36px",
  padding: "0 12px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  cursor: "pointer",
};

const paginationButtonDisabledStyle: React.CSSProperties = {
  opacity: 0.45,
  cursor: "not-allowed",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "32px 20px",
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

function assignedDriverLabel(delivery: DeliveryRaw, drivers: DriverOption[]) {
  const driverId =
    delivery.driver_id !== null && delivery.driver_id !== undefined
      ? Number(delivery.driver_id)
      : null;

  const assignedDriver = driverId
    ? drivers.find((driver) => Number(driver.id) === driverId)
    : null;

  const fullName = [
    safeString(delivery.assigned_driver_first_name),
    safeString(delivery.assigned_driver_last_name),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    assignedDriver?.displayName ||
    fullName ||
    safeString(delivery.assigned_driver_name) ||
    safeString(delivery.assigned_driver_username) ||
    safeString(delivery.cadete) ||
    "—"
  );
}

function safeString(value: unknown) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function safeLower(value: unknown) {
  return safeString(value).toLowerCase();
}

// "Hoy" operativo en hora Argentina, nunca Date local del browser (mismo
// motivo y mismo patrón que EnviosLegacy.tsx: un usuario fuera de
// Argentina tendría un "hoy" distinto al que ya usa fecha_wynflex en el
// backend). No hay date-util compartido entre pantallas en src/ hoy, así
// que se reutiliza el mismo patrón puntual acá en vez de importarlo de
// otra pantalla.
const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";

function getArgentinaDateKey(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ARGENTINA_TZ }).format(new Date());
}

function todayInputValue() {
  return getArgentinaDateKey();
}

function normalizeDateInput(value: unknown) {
  const raw = safeString(value);
  if (!raw) return "";

  // Si el backend ya manda una fecha ISO/SQL, usamos la parte YYYY-MM-DD sin pasar
  // por Date para evitar corrimientos por zona horaria.
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // Soporte defensivo para fechas tipo 29/6/2026 o 29/06/26.
  const argMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (argMatch) {
    const day = argMatch[1].padStart(2, "0");
    const month = argMatch[2].padStart(2, "0");
    const year = argMatch[3].length === 2 ? `20${argMatch[3]}` : argMatch[3];
    return `${year}-${month}-${day}`;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDate(value: string) {
  const raw = safeString(value);
  if (!raw) return "—";

  const sqlMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);

  if (sqlMatch) {
    const [, year, month, day, hour, minute] = sqlMatch;
    const date = `${day}/${month}/${year}`;
    const time = hour && minute ? ` ${hour}:${minute}` : "";

    return `${date}${time}`;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isDateWithinRange(value: string, from: string, to: string) {
  const normalized = normalizeDateInput(value);
  if (!normalized) return false;
  if (from && normalized < from) return false;
  if (to && normalized > to) return false;

  return true;
}

function resolveSaleDate(delivery: DeliveryRaw) {
  return (
    safeString(delivery.fecha_venta) ||
    safeString(delivery.sale_date) ||
    safeString(delivery.date_created) ||
    safeString(delivery.order_date) ||
    safeString(delivery.created_at) ||
    safeString(delivery.fecha_wynflex) ||
    safeString(delivery.updated_at)
  );
}

function resolveCollectionDate(delivery: DeliveryRaw) {
  return (
    safeString(delivery.fecha_colecta) ||
    safeString(delivery.collection_date)
  );
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

function toArray<T>(value: T[] | T | undefined | null): T[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
}

function extractDeliveryRows(payload: DeliveriesResponse): DeliveryRaw[] {
  if (Array.isArray(payload.deliveries)) return payload.deliveries;
  if (Array.isArray(payload.delivery)) return payload.delivery;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.result)) return payload.result;
  if (payload.deliveries && typeof payload.deliveries === "object") {
    return [payload.deliveries];
  }
  if (payload.delivery && typeof payload.delivery === "object") {
    return [payload.delivery];
  }
  return [];
}

function toStoreArray(payload: StoresResponse): StoreRaw[] {
  return payload.stores || payload.data || payload.result || [];
}

function toUserArray(payload: UsersResponse): UserRaw[] {
  return payload.usuarios || payload.users || payload.data || payload.result || [];
}

function toDriverArray(payload: DriversResponse): DriverRaw[] {
  if (Array.isArray(payload.drivers)) return payload.drivers;
  if (Array.isArray(payload.driver)) return payload.driver;
  if (payload.driver && typeof payload.driver === "object") return [payload.driver];
  return payload.data || payload.result || [];
}

function normalizeStoreName(store: StoreRaw) {
  return store.nombre_fantasia || store.nombreFantasia || store.name || "—";
}

function resolveAccountName(delivery: DeliveryRaw) {
  return (
    safeString(delivery.account_name) ||
    safeString(delivery.account_nombre_fantasia) ||
    safeString(delivery.account_nombre) ||
    safeString(delivery.accountName) ||
    safeString(delivery.account) ||
    // En el listado viejo este campo venía funcionando visualmente como cuenta/integración.
    safeString(delivery.nombre_fantasia) ||
    ""
  );
}

function normalizeUserName(user: UserRaw) {
  const fullName = [safeString(user.first_name), safeString(user.last_name)]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    fullName ||
    safeString(user.name) ||
    safeString(user.username) ||
    `Usuario ${user.id ?? "—"}`
  );
}

function getDriverOptionId(user: UserRaw): number | null {
  const value =
    user.driver_id ??
    user.driverDataId ??
    user.driver_data_id ??
    user.driver_data?.id ??
    user.id;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getUserId(user: UserRaw): number | null {
  const parsed = Number(user.user_id ?? user.id);
  return Number.isFinite(parsed) ? parsed : null;
}

function isDriverUser(user: UserRaw) {
  const normalizedRole = safeLower(user.rol || user.role);
  return (
    normalizedRole.includes("driver") ||
    normalizedRole.includes("chofer") ||
    Boolean(user.driver_id || user.driver_data_id || user.driver_data?.id)
  );
}

function normalizeDriverOption(user: UserRaw): DriverOption | null {
  const id = getDriverOptionId(user);
  if (!id) return null;

  const vehicleType =
    safeString(user.vehicle_type) ||
    safeString(user.vehicleType) ||
    safeString(user.driver_data?.vehicle_type) ||
    safeString(user.driver_data?.vehicleType);

  const vehiclePlate =
    safeString(user.vehicle_plate) ||
    safeString(user.vehiclePlate) ||
    safeString(user.patente) ||
    safeString(user.driver_data?.vehicle_plate) ||
    safeString(user.driver_data?.vehiclePlate) ||
    safeString(user.driver_data?.patente);

const displayName = normalizeUserName(user);

return {
  id,
  userId: getUserId(user),
  displayName,
  label: displayName,
};
}

function normalizeDriverRaw(
  driver: DriverRaw,
  usersById: Map<string, UserRaw>,
): DriverOption | null {
  const id = Number(driver.id);
  if (!Number.isFinite(id)) return null;

  const userId =
    driver.user_id !== null && driver.user_id !== undefined
      ? Number(driver.user_id)
      : null;

  const user = userId !== null ? usersById.get(String(userId)) : undefined;

  const vehicleType =
    safeString(driver.vehiculo) ||
    safeString(driver.vehicle_type) ||
    safeString(driver.vehicleType);

  const vehiclePlate =
    safeString(driver.patente) ||
    safeString(driver.vehicle_plate) ||
    safeString(driver.vehiclePlate);

const displayName = user ? normalizeUserName(user) : `Driver ${id}`;

return {
  id,
  userId: userId !== null && Number.isFinite(userId) ? userId : null,
  displayName,
  label: displayName,
};
}

function uniqueDriverOptions(drivers: DriverOption[]) {
  const byId = new Map<number, DriverOption>();

  drivers.forEach((driver) => {
    if (!byId.has(driver.id)) byId.set(driver.id, driver);
  });

  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function getStatusTone(
  status: string,
): "neutral" | "primary" | "success" | "warning" | "danger" {
  const normalized = safeLower(status);

  if (
    normalized.includes("delivered") ||
    normalized.includes("entregado") ||
    normalized.includes("completed")
  ) {
    return "success";
  }

  if (
    normalized.includes("cancel") ||
    normalized.includes("returned") ||
    normalized.includes("fallido") ||
    normalized.includes("not_delivered")
  ) {
    return "danger";
  }

  if (
    normalized.includes("ready") ||
    normalized.includes("pickup") ||
    normalized.includes("route") ||
    normalized.includes("camino") ||
    normalized.includes("handling") ||
    normalized.includes("assigned")
  ) {
    return "warning";
  }

  if (normalized.includes("pending") || normalized.includes("created")) {
    return "primary";
  }

  return "neutral";
}

function badgeStyle(
  tone: "neutral" | "primary" | "success" | "warning" | "danger",
): React.CSSProperties {
  const palette = {
    neutral: {
      background: "rgba(255,255,255,0.06)",
      color: "var(--text-primary)",
      border: "1px solid var(--border)",
    },
    primary: {
      background: "var(--primary-soft)",
      color: "var(--text-primary)",
      border: "1px solid var(--border)",
    },
    success: {
      background: "rgba(107,162,125,0.14)",
      color: "var(--success)",
      border: "1px solid rgba(107,162,125,0.24)",
    },
    warning: {
      background: "rgba(200,144,52,0.14)",
      color: "var(--warning)",
      border: "1px solid rgba(200,144,52,0.24)",
    },
    danger: {
      background: "rgba(185,100,95,0.14)",
      color: "var(--danger)",
      border: "1px solid rgba(185,100,95,0.24)",
    },
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "30px",
    padding: "0 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    ...palette[tone],
  };
}

function formatCurrency(value: number | null) {
  if (value === null) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value);
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={selectBaseStyle}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function DriverCombobox({
  label,
  value,
  drivers,
  disabled,
  placeholder = "Buscar driver...",
  onChange,
}: {
  label?: string;
  value: string;
  drivers: DriverOption[];
  disabled?: boolean;
  placeholder?: string;
  onChange: (driverId: string) => void;
}) {
  const selectedDriver = drivers.find((driver) => String(driver.id) === value);
  const [query, setQuery] = useState(selectedDriver?.label || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const currentDriver = drivers.find((driver) => String(driver.id) === value);
    setQuery(currentDriver?.label || "");
  }, [drivers, value]);

  const visibleDrivers = useMemo(() => {
    const normalizedQuery = safeLower(query);
    if (!normalizedQuery) return drivers.slice(0, 40);

    return drivers
      .filter((driver) => safeLower(driver.label).includes(normalizedQuery))
      .slice(0, 40);
  }, [drivers, query]);

  const selectDriver = (driver: DriverOption) => {
    onChange(String(driver.id));
    setQuery(driver.label);
    setOpen(false);
  };

  return (
    <label style={labelStyle}>
      {label ? <span style={labelTextStyle}>{label}</span> : null}

      <div style={{ position: "relative", width: "100%" }}>
        <input
          value={query}
          disabled={disabled}
          placeholder={drivers.length ? placeholder : "Sin drivers"}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            if (!event.target.value) onChange("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
          style={{
            ...selectBaseStyle,
            minHeight: "42px",
            borderRadius: "14px",
            background: "var(--surface)",
            color: "var(--text-primary)",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.68 : 1,
          }}
        />

        {open && !disabled ? (
          <div
            style={{
              position: "absolute",
              zIndex: 20,
              left: 0,
              right: 0,
              top: "calc(100% + 6px)",
              maxHeight: "240px",
              overflowY: "auto",
              border: "1px solid var(--border)",
              borderRadius: "14px",
              background: "var(--surface-alt)",
              boxShadow: "var(--shadow-lg)",
              padding: "6px",
            }}
          >
            {visibleDrivers.length ? (
              visibleDrivers.map((driver) => (
                <button
                  key={driver.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectDriver(driver);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderRadius: "10px",
                    background:
                      String(driver.id) === value
                        ? "var(--primary-soft)"
                        : "transparent",
                    color: "var(--text-primary)",
                    padding: "10px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  {driver.label}
                </button>
              ))
            ) : (
              <div style={{ ...mutedTextStyle, padding: "10px 12px" }}>
                Sin resultados
              </div>
            )}
          </div>
        ) : null}
      </div>
    </label>
  );
}

export default function Deliveries() {
  const { token } = useAuth();

  const [deliveries, setDeliveries] = useState<DeliveryView[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [storeOptions, setStoreOptions] = useState<string[]>(["Todos"]);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [bulkDriverId, setBulkDriverId] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DeliveryView | null>(null);
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Arranca en "Hoy" (fecha operativa AR) -- nunca sin filtro -- para no
  // descargar el historial completo en la carga inicial. initialFilters
  // (dateFrom/dateTo vacíos = "Todos") se deja intacto: resetFilters()
  // sigue pudiendo volver explícitamente a "Todos".
  const todayFilters: Filters = {
    ...initialFilters,
    dateFrom: getArgentinaDateKey(),
    dateTo: getArgentinaDateKey(),
  };
  const [draftFilters, setDraftFilters] = useState<Filters>(todayFilters);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(todayFilters);

  // Evita que una respuesta vieja (de un filtro de fecha ya reemplazado)
  // pise a la más nueva si llega tarde -- solo se aplica la última
  // solicitud en vuelo.
  const loadRequestRef = useRef(0);

  const loadData = async (dateFrom: string, dateTo: string) => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    setError(null);

    try {
      const deliveriesParams = new URLSearchParams();
      if (dateFrom) deliveriesParams.set("date_from", dateFrom);
      if (dateTo) deliveriesParams.set("date_to", dateTo);
      const deliveriesQuery = deliveriesParams.toString();
      const deliveriesUrl = deliveriesQuery
        ? `/v2/deliveries?${deliveriesQuery}`
        : "/v2/deliveries";

      const [deliveriesRes, storesRes, usersRes, driversRes] =
        await Promise.all([
          authFetch(deliveriesUrl, { token }),
          authFetch("/v2/stores", { token }),
          authFetch("/v2/users", { token }),
          authFetch("/v2/drivers", { token }),
        ]);

      if (requestId !== loadRequestRef.current) return;

      const deliveriesJson: DeliveriesResponse = await deliveriesRes
        .json()
        .catch(() => ({}));
      const storesJson: StoresResponse = await storesRes.json().catch(() => ({}));
      const usersJson: UsersResponse = await usersRes.json().catch(() => ({}));
      const driversJson: DriversResponse = await driversRes.json().catch(() => ({}));

      if (!deliveriesRes.ok || deliveriesJson?.ok === false) {
        throw new Error(deliveriesJson?.error || "No se pudieron cargar los envíos");
      }

      if (!storesRes.ok || storesJson?.ok === false) {
        throw new Error(storesJson?.error || "No se pudieron cargar las stores");
      }

      if (!usersRes.ok || usersJson?.ok === false) {
        throw new Error(usersJson?.error || "No se pudieron cargar los usuarios");
      }

      if (!driversRes.ok || driversJson?.ok === false) {
        throw new Error(driversJson?.error || "No se pudieron cargar los drivers");
      }

      const users = toUserArray(usersJson);
      const usersById = new Map<string, UserRaw>();

      users.forEach((user) => {
        if (user.id !== null && user.id !== undefined) {
          usersById.set(String(user.id), user);
        }
      });

      const driverOptionsFromDrivers = toDriverArray(driversJson)
        .map((driver) => normalizeDriverRaw(driver, usersById))
        .filter((driver): driver is DriverOption => Boolean(driver));

      const driverOptionsFromUsers = users
        .filter(isDriverUser)
        .map(normalizeDriverOption)
        .filter((driver): driver is DriverOption => Boolean(driver));

      const driverOptions = uniqueDriverOptions(
        driverOptionsFromDrivers.length
          ? driverOptionsFromDrivers
          : driverOptionsFromUsers,
      );

      setDrivers(driverOptions);

      const storesMap = new Map<string, string>();
      const normalizedStores = toStoreArray(storesJson);

      normalizedStores.forEach((store) => {
        if (store?.id !== null && store?.id !== undefined) {
          storesMap.set(String(store.id), normalizeStoreName(store));
        }
      });

      const nextStoreOptions = Array.from(
        new Set(normalizedStores.map(normalizeStoreName).filter((name) => name && name !== "—")),
      ).sort((a, b) => a.localeCompare(b));

      setStoreOptions(["Todos", ...nextStoreOptions]);

      const deliveryRows = extractDeliveryRows(deliveriesJson);
      const statusRows = toArray(deliveriesJson.status);
      const financialRows = toArray(deliveriesJson.financials);

      const statusesByDeliveryId = new Map<number, DeliveryStatusRaw[]>();

      statusRows.forEach((status) => {
        const deliveryId = Number(status.delivery_id);
        if (!Number.isFinite(deliveryId)) return;

        const list = statusesByDeliveryId.get(deliveryId) || [];
        list.push(status);
        statusesByDeliveryId.set(deliveryId, list);
      });

      const financialByDeliveryId = new Map<number, DeliveryFinancialRaw>();

      financialRows.forEach((financial) => {
        const deliveryId = Number(financial.delivery_id);
        if (!Number.isFinite(deliveryId)) return;

        financialByDeliveryId.set(deliveryId, financial);
      });

      const backendIsFlattened =
        deliveryRows.length > 0 &&
        deliveryRows.some(
          (row) =>
            row.status !== undefined ||
            row.status_ml !== undefined ||
            row.precio_cliente !== undefined ||
            row.precio_chofer !== undefined ||
            row.total !== undefined,
        );

      const merged = deliveryRows
        .filter((delivery) => Number.isFinite(Number(delivery.id)))
        .map((delivery) => {
          const id = Number(delivery.id);
          const statuses = statusesByDeliveryId.get(id) || [];
          const latestStatus = statuses[statuses.length - 1];
          const financial = financialByDeliveryId.get(id);

          const resolvedAccountName = resolveAccountName(delivery);

          const resolvedStoreName =
            storesMap.get(String(delivery.store_id ?? "")) ||
            "—";

          const status = backendIsFlattened
            ? safeString(delivery.status_ml) ||
              safeString(delivery.status) ||
              "Sin estado"
            : safeString(latestStatus?.status_ml) ||
              safeString(latestStatus?.status) ||
              "Sin estado";

          return {
            id,
            driver_id:
  delivery.driver_id !== null && delivery.driver_id !== undefined
    ? Number(delivery.driver_id)
    : delivery.assigned_driver_id !== null && delivery.assigned_driver_id !== undefined
      ? Number(delivery.assigned_driver_id)
      : null,
            seller_id:
              delivery.seller_id !== null && delivery.seller_id !== undefined
                ? Number(delivery.seller_id)
                : null,
            store_id:
              delivery.store_id !== null && delivery.store_id !== undefined
                ? Number(delivery.store_id)
                : null,
            account_id:
              delivery.account_id !== null && delivery.account_id !== undefined
                ? Number(delivery.account_id)
                : null,
            account_name: resolvedAccountName ||
              (delivery.account_id !== null && delivery.account_id !== undefined
                ? `Cuenta #${delivery.account_id}`
                : "—"),
            store_name: resolvedStoreName,
            id_venta_ml: safeString(delivery.id_venta_ml) || "—",
            tracking: safeString(delivery.tracking) || "—",
            origen: safeString(delivery.origen) || "—",
            nombre_destinatario: safeString(delivery.nombre_destinatario) || "—",
            direccion: safeString(delivery.direccion) || "—",
            codigo_postal: safeString(delivery.codigo_postal) || "—",
            localidad:
              safeString(delivery.provincia) ||
              safeString(delivery.localidad) ||
              "—",
            provincia: safeString(delivery.provincia) || "—",
            zona_entrega: "",
            tipo_envio: safeString(delivery.tipo_envio) || "—",
            is_turbo: Boolean(delivery.is_turbo),
            chofer: assignedDriverLabel(delivery, driverOptions),
            status,
            status_updated_by: backendIsFlattened
              ? safeString(delivery.updated_by) || "—"
              : safeString(latestStatus?.updated_by) || "—",
            fecha_wynflex: backendIsFlattened
              ? safeString(delivery.fecha_wynflex)
              : safeString(latestStatus?.fecha_wynflex),
            fecha_venta: resolveSaleDate(delivery),
            fecha_colecta: resolveCollectionDate(delivery),
            updated_at: backendIsFlattened
              ? safeString(delivery.updated_at)
              : safeString(latestStatus?.updated_at),
            precio_cliente: backendIsFlattened
              ? toNumber(delivery.precio_cliente)
              : toNumber(financial?.precio_cliente),
            precio_chofer: backendIsFlattened
              ? toNumber(delivery.precio_chofer)
              : toNumber(financial?.precio_chofer),
            telefono_destinatario: safeString(delivery.telefono_destinatario),
            email_destinatario: safeString(delivery.email_destinatario),
            comentario_destino: safeString(delivery.comentario_destino),
          };
        });

      setDeliveries(
        merged.sort((a, b) => {
          const dateA = normalizeDateInput(a.fecha_venta || a.fecha_wynflex || a.updated_at);
          const dateB = normalizeDateInput(b.fecha_venta || b.fecha_wynflex || b.updated_at);

          if (dateA && dateB && dateA !== dateB) return dateB.localeCompare(dateA);
          if (dateA && !dateB) return -1;
          if (!dateA && dateB) return 1;

          return b.id - a.id;
        }),
      );
    } catch (err) {
      if (requestId !== loadRequestRef.current) return;
      setError(err instanceof Error ? err.message : "Error cargando envíos");
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadData(appliedFilters.dateFrom, appliedFilters.dateTo);
  }, [token, appliedFilters.dateFrom, appliedFilters.dateTo]);

  const estadoOptions = useMemo(() => {
    const values = Array.from(
      new Set(deliveries.map((item) => item.status).filter(Boolean)),
    ).sort();

    return ["Todos", ...values];
  }, [deliveries]);

  const origenOptions = useMemo(() => {
    const values = Array.from(
      new Set(deliveries.map((item) => item.origen).filter(Boolean)),
    ).sort();

    return ["Todos", ...values];
  }, [deliveries]);

  const tipoEnvioOptions = useMemo(() => {
    const values = Array.from(
      new Set(deliveries.map((item) => item.tipo_envio).filter(Boolean)),
    ).sort();

    return ["Todos", ...values];
  }, [deliveries]);

  const filteredRows = useMemo(() => {
    return deliveries.filter((row) => {
      if (appliedFilters.estado !== "Todos" && row.status !== appliedFilters.estado) {
        return false;
      }

      if (appliedFilters.origen !== "Todos" && row.origen !== appliedFilters.origen) {
        return false;
      }

      if (
        appliedFilters.tipoEnvio !== "Todos" &&
        row.tipo_envio !== appliedFilters.tipoEnvio
      ) {
        return false;
      }

      if (appliedFilters.turbo === "Sí" && !row.is_turbo) return false;
      if (appliedFilters.turbo === "No" && row.is_turbo) return false;

      if (
        appliedFilters.tracking &&
        !safeLower(row.tracking).includes(safeLower(appliedFilters.tracking))
      ) {
        return false;
      }

      if (
        appliedFilters.storeName &&
        appliedFilters.storeName !== "Todos" &&
        row.store_name !== appliedFilters.storeName
      ) {
        return false;
      }

      if (
        (appliedFilters.dateFrom || appliedFilters.dateTo) &&
        !isDateWithinRange(
          row.fecha_venta || row.fecha_wynflex || row.updated_at,
          appliedFilters.dateFrom,
          appliedFilters.dateTo,
        )
      ) {
        return false;
      }

      if (
        appliedFilters.destinatario &&
        !safeLower(row.nombre_destinatario).includes(
          safeLower(appliedFilters.destinatario),
        )
      ) {
        return false;
      }

      if (
        appliedFilters.localidad &&
        !safeLower(row.localidad).includes(safeLower(appliedFilters.localidad))
      ) {
        return false;
      }

      if (
        appliedFilters.codigoPostal &&
        !safeLower(row.codigo_postal).includes(
          safeLower(appliedFilters.codigoPostal),
        )
      ) {
        return false;
      }

      if (
        appliedFilters.chofer &&
        !safeLower(row.chofer).includes(safeLower(appliedFilters.chofer))
      ) {
        return false;
      }

      if (appliedFilters.search) {
        const haystack = [
          row.id,
          row.tracking,
          row.store_name,
          row.account_name,
          row.nombre_destinatario,
          row.direccion,
          row.codigo_postal,
          row.localidad,
          row.provincia,
          row.chofer,
          row.status,
          row.tipo_envio,
        ]
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(safeLower(appliedFilters.search))) return false;
      }

      return true;
    });
  }, [deliveries, appliedFilters]);

  const totalRows = deliveries.length;
  const visibleRows = filteredRows.length;
  const entregadosRows = filteredRows.filter(
    (row) => getStatusTone(row.status) === "success",
  ).length;
  const pendientesRows = Math.max(visibleRows - entregadosRows, 0);

  const totalPages = useMemo(() => {
    if (pageSize === 0) return 1;
    return Math.max(1, Math.ceil(filteredRows.length / pageSize));
  }, [filteredRows.length, pageSize]);

  const paginatedRows = useMemo(() => {
    if (pageSize === 0) return filteredRows;
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // QR de la etiqueta de ML de los envíos de esta página (se vuelven a pedir
  // cada vez que la lista se recarga).
  const labelQrs = useLabelQrs(paginatedRows.map((row) => row.id), token, deliveries);

  const paginationStart =
    filteredRows.length === 0
      ? 0
      : pageSize === 0
        ? 1
        : (currentPage - 1) * pageSize + 1;

  const paginationEnd =
    pageSize === 0
      ? filteredRows.length
      : Math.min(currentPage * pageSize, filteredRows.length);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedDeliveryIds(new Set());
  }, [appliedFilters, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const visibleSelectedCount = useMemo(
    () => paginatedRows.filter((row) => selectedDeliveryIds.has(row.id)).length,
    [paginatedRows, selectedDeliveryIds],
  );

  const selectedRowsCount = selectedDeliveryIds.size;

  const allVisibleRowsSelected =
    paginatedRows.length > 0 && visibleSelectedCount === paginatedRows.length;

  const toggleDeliverySelection = (deliveryId: number) => {
    setSelectedDeliveryIds((prev) => {
      const next = new Set(prev);

      if (next.has(deliveryId)) next.delete(deliveryId);
      else next.add(deliveryId);

      return next;
    });
  };

  const toggleCurrentPageSelection = () => {
    setSelectedDeliveryIds((prev) => {
      const next = new Set(prev);

      if (allVisibleRowsSelected) {
        paginatedRows.forEach((row) => next.delete(row.id));
      } else {
        paginatedRows.forEach((row) => next.add(row.id));
      }

      return next;
    });
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const applyTodayFilter = () => {
    const today = todayInputValue();
    const nextFilters = {
      ...draftFilters,
      dateFrom: today,
      dateTo: today,
    };

    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const resetFilters = () => {
    setDraftFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  const assignDriverRequest = async (deliveryId: number, driverId: number) => {
    const response = await authFetch(`/v2/deliveries/${deliveryId}/driver`, {
      method: "PUT",
      token,
      json: true,
      body: JSON.stringify({ driverId }),
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok || json?.ok === false) {
      throw new Error(json?.error || "No se pudo asignar el driver");
    }
  };

  const updateAssignedDriverLocally = (deliveryIds: number[], driverId: number) => {
    const selectedDriver = drivers.find((driver) => driver.id === driverId);
    const nextDriverName = selectedDriver?.displayName || "Driver asignado";
    const deliveryIdSet = new Set(deliveryIds);

    setDeliveries((prev) =>
      prev.map((delivery) =>
        deliveryIdSet.has(delivery.id)
          ? {
              ...delivery,
              driver_id: driverId,
              chofer: nextDriverName,
            }
          : delivery,
      ),
    );

    setSelectedRow((prev) =>
      prev && deliveryIdSet.has(prev.id)
        ? {
            ...prev,
            driver_id: driverId,
            chofer: nextDriverName,
          }
        : prev,
    );
  };

  const handleBulkAssignDriver = async () => {
    const driverId = Number(bulkDriverId);
    if (!Number.isFinite(driverId) || !selectedRowsCount) return;

    const deliveryIds = Array.from(selectedDeliveryIds);

    setBulkAssigning(true);
    setError(null);

    try {
      await Promise.all(
        deliveryIds.map((deliveryId) => assignDriverRequest(deliveryId, driverId)),
      );

      updateAssignedDriverLocally(deliveryIds, driverId);
      setSelectedDeliveryIds(new Set());
      setBulkDriverId("");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error asignando driver a los envíos seleccionados",
      );
    } finally {
      setBulkAssigning(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "id",
      "tracking",
      "store_name",
      "account_name",
      "fecha_venta",
      "fecha_colecta",
      "nombre_destinatario",
      "direccion",
      "codigo_postal",
      "localidad",
      "provincia",
      "chofer",
      "status",
      "tipo_envio",
      "is_turbo",
    ];

    const content = [
      headers.join(","),
      ...filteredRows.map((row) =>
        headers
          .map((header) => {
            const raw = row[header as keyof DeliveryView];
            const value = raw === null || raw === undefined ? "" : String(raw);
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob(["\ufeff" + content], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `lynx-envios-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <h1 style={titleStyle}>Envíos</h1>

        <div style={actionsWrapStyle}>
          <Button
            variant="secondary"
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            {filtersOpen ? "Ocultar filtros" : "Filtros"}
          </Button>

          <Button
            variant="secondary"
            onClick={exportCsv}
            disabled={!filteredRows.length}
          >
            Descargar CSV
          </Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="deliveries-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Total</div>
          <div style={kpiValueStyle}>{totalRows}</div>
          <div style={kpiMetaStyle}>Todos los envíos cargados</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Visibles</div>
          <div style={kpiValueStyle}>{visibleRows}</div>
          <div style={kpiMetaStyle}>Resultado según filtros</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Entregados</div>
          <div style={kpiValueStyle}>{entregadosRows}</div>
          <div style={kpiMetaStyle}>Según estado</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Pendientes</div>
          <div style={kpiValueStyle}>{pendientesRows}</div>
          <div style={kpiMetaStyle}>Operación en curso</div>
        </Card>
      </div>

      {filtersOpen ? (
        <Card>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "14px",
            }}
            className="deliveries-filters-grid"
          >
            <SelectField
              label="Estado"
              value={draftFilters.estado}
              onChange={(value) =>
                setDraftFilters((prev) => ({ ...prev, estado: value }))
              }
              options={estadoOptions}
            />

            <SelectField
              label="Origen"
              value={draftFilters.origen}
              onChange={(value) =>
                setDraftFilters((prev) => ({ ...prev, origen: value }))
              }
              options={origenOptions}
            />

            <SelectField
              label="Tipo envío"
              value={draftFilters.tipoEnvio}
              onChange={(value) =>
                setDraftFilters((prev) => ({ ...prev, tipoEnvio: value }))
              }
              options={tipoEnvioOptions}
            />

            <SelectField
              label="Turbo"
              value={draftFilters.turbo}
              onChange={(value) =>
                setDraftFilters((prev) => ({ ...prev, turbo: value }))
              }
              options={["Todos", "Sí", "No"]}
            />

            <Input
              label="Tracking"
              placeholder="Buscar tracking"
              value={draftFilters.tracking}
              onChange={(event) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  tracking: event.target.value,
                }))
              }
            />

            <SelectField
              label="Tienda"
              value={draftFilters.storeName || "Todos"}
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  storeName: value,
                }))
              }
              options={storeOptions}
            />

            <label style={labelStyle}>
              <span style={labelTextStyle}>Fecha desde</span>
              <input
                type="date"
                value={draftFilters.dateFrom}
                onChange={(event) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    dateFrom: event.target.value,
                  }))
                }
                style={selectBaseStyle}
              />
            </label>

            <label style={labelStyle}>
              <span style={labelTextStyle}>Fecha hasta</span>
              <input
                type="date"
                value={draftFilters.dateTo}
                onChange={(event) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    dateTo: event.target.value,
                  }))
                }
                style={selectBaseStyle}
              />
            </label>

            <Input
              label="Comprador"
              placeholder="Nombre del comprador"
              value={draftFilters.destinatario}
              onChange={(event) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  destinatario: event.target.value,
                }))
              }
            />

            <Input
              label="Chofer"
              placeholder="Chofer asignado"
              value={draftFilters.chofer}
              onChange={(event) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  chofer: event.target.value,
                }))
              }
            />

            <Input
              label="Localidad"
              placeholder="Localidad"
              value={draftFilters.localidad}
              onChange={(event) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  localidad: event.target.value,
                }))
              }
            />

            <Input
              label="Código postal"
              placeholder="CP"
              value={draftFilters.codigoPostal}
              onChange={(event) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  codigoPostal: event.target.value,
                }))
              }
            />

            <div style={{ gridColumn: "span 2" }}>
              <Input
                label="Búsqueda global"
                placeholder="Buscar en varios campos"
                value={draftFilters.search}
                onChange={(event) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div
            style={{
              marginTop: "18px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button variant="secondary" onClick={applyTodayFilter}>
              Hoy
            </Button>

            <Button variant="ghost" onClick={resetFilters}>
              Restablecer
            </Button>

            <Button variant="primary" onClick={applyFilters}>
              Aplicar filtros
            </Button>
          </div>
        </Card>
      ) : null}

      <Card style={{ padding: "0", overflow: "visible" }}>
        <div style={bulkAssignBarStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: "360px" }}>
              <DriverCombobox
                label="Asignación de driver"
                value={bulkDriverId}
                drivers={drivers}
                disabled={bulkAssigning || !drivers.length || !selectedRowsCount}
                placeholder="Buscar driver..."
                onChange={setBulkDriverId}
              />
            </div>

            <Button
              variant="primary"
              onClick={handleBulkAssignDriver}
              disabled={
                bulkAssigning ||
                !selectedRowsCount ||
                !bulkDriverId ||
                !drivers.length
              }
            >
              {bulkAssigning
                ? "Asignando..."
                : selectedRowsCount
                  ? `Asignar a ${selectedRowsCount} envío${
                      selectedRowsCount === 1 ? "" : "s"
                    }`
                  : "Seleccioná envíos"}
            </Button>

            {selectedRowsCount ? (
              <Button
                variant="ghost"
                onClick={() => setSelectedDeliveryIds(new Set())}
                disabled={bulkAssigning}
              >
                Limpiar selección
              </Button>
            ) : null}
          </div>

          <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
            {selectedRowsCount
              ? `${selectedRowsCount} seleccionado${
                  selectedRowsCount === 1 ? "" : "s"
                }`
              : "Seleccioná uno o varios envíos desde la tabla"}
          </div>
        </div>

        {loading ? (
          <div style={emptyStateStyle}>Cargando envíos...</div>
        ) : error ? (
          <div style={{ ...emptyStateStyle, color: "var(--danger)" }}>
            {error}
          </div>
        ) : !filteredRows.length ? (
          <div style={emptyStateStyle}>
            No hay envíos para los filtros seleccionados.
          </div>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: "48px" }}>
                    <input
                      type="checkbox"
                      checked={allVisibleRowsSelected}
                      onChange={toggleCurrentPageSelection}
                      onClick={(event) => event.stopPropagation()}
                      style={checkboxStyle}
                      aria-label="Seleccionar envíos visibles"
                    />
                  </th>
                  <th style={{ ...thStyle, width: "70px" }}>QR</th>
                  <th style={thStyle}>Tracking</th>
                  <th style={thStyle}>Tienda</th>
                  <th style={thStyle}>Comprador</th>
                  <th style={thStyle}>Dirección</th>
                  <th style={thStyle}>CP</th>
                  <th style={thStyle}>Localidad</th>
                  <th style={thStyle}>Zona de entrega</th>
                  <th style={thStyle}>Chofer asignado</th>
                  <th style={thStyle}>Estado</th>
                </tr>
              </thead>

              <tbody>
                {paginatedRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedRow(row)}
                    style={{
                      cursor: "pointer",
                      transition: "background var(--transition-base)",
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.background =
                        "var(--surface-alt)";
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td
                      style={tdStyle}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDeliveryIds.has(row.id)}
                        onChange={() => toggleDeliverySelection(row.id)}
                        style={checkboxStyle}
                        aria-label={`Seleccionar envío ${row.id}`}
                      />
                    </td>

                    <td style={tdStyle}>
                      <LabelQrCell qr={labelQrs[row.id]} tracking={row.tracking} />
                    </td>

                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{row.tracking || "—"}</div>
                      <div style={mutedTextStyle}>#{row.id}</div>
                    </td>

                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>
                        {row.store_name || "—"}
                      </div>
                    </td>

                    <td style={tdStyle}>{row.nombre_destinatario || "—"}</td>
                    <td style={tdStyle}>{row.direccion || "—"}</td>
                    <td style={tdStyle}>{row.codigo_postal || "—"}</td>
                    <td style={tdStyle}>{row.localidad || "—"}</td>

                    <td style={tdStyle}>
                      <span style={badgeStyle("neutral")}>
                        {row.zona_entrega || "—"}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      {row.chofer && row.chofer !== "—" ? (
                        <span style={badgeStyle("primary")}>{row.chofer}</span>
                      ) : (
                        <span style={badgeStyle("neutral")}>Sin asignar</span>
                      )}
                    </td>

                    <td style={tdStyle}>
                      <span style={badgeStyle(getStatusTone(row.status))}>
                        {row.status || "Sin estado"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && filteredRows.length ? (
          <div style={bottomPaginationStyle}>
            <label style={{ ...labelStyle, width: "160px" }}>
              <span style={labelTextStyle}>Mostrar</span>
              <select
                value={pageSize === 0 ? "all" : String(pageSize)}
                onChange={(event) => {
                  const value = event.target.value;
                  setPageSize(value === "all" ? 0 : Number(value));
                }}
                style={{ ...selectBaseStyle, minHeight: "42px" }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">Todos</option>
              </select>
            </label>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
                color: "var(--text-secondary)",
                fontSize: "13px",
              }}
            >
              <span>
                Mostrando {paginationStart}–{paginationEnd} de{" "}
                {filteredRows.length}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={pageSize === 0 || currentPage <= 1}
                style={{
                  ...paginationButtonStyle,
                  ...(pageSize === 0 || currentPage <= 1
                    ? paginationButtonDisabledStyle
                    : {}),
                }}
              >
                Anterior
              </button>

              <span>
                Página {pageSize === 0 ? 1 : currentPage} de {totalPages}
              </span>

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={pageSize === 0 || currentPage >= totalPages}
                style={{
                  ...paginationButtonStyle,
                  ...(pageSize === 0 || currentPage >= totalPages
                    ? paginationButtonDisabledStyle
                    : {}),
                }}
              >
                Siguiente
              </button>
            </div>
          </div>
        ) : null}
      </Card>

      {selectedRow ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            background: "rgba(0,0,0,0.65)",
          }}
        >
          <div
            style={{ position: "absolute", inset: 0 }}
            onClick={() => setSelectedRow(null)}
          />

          <Card
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "760px",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div style={headerRowStyle}>
              <div>
                <h2 style={{ margin: 0, color: "var(--text-primary)" }}>
                  Envío #{selectedRow.id}
                </h2>
                <div style={mutedTextStyle}>{selectedRow.store_name}</div>
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                {/* Etiqueta propia: la usa el bulto que no vino de Mercado
                    Libre y llego sin nada pegado. Si el envio ya tiene codigo,
                    reimprime ese mismo. */}
                <Button
                  variant="secondary"
                  onClick={async () => {
                    setError(null);
                    try {
                      await printPackageLabel(selectedRow.id, { token });
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "No se pudo imprimir la etiqueta");
                    }
                  }}
                >
                  Imprimir etiqueta
                </Button>

                <Button variant="ghost" onClick={() => setSelectedRow(null)}>
                  Cerrar
                </Button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "14px",
                marginTop: "18px",
              }}
            >
              {[
                ["Tracking", selectedRow.tracking],
                ["ID venta ML", selectedRow.id_venta_ml],
                ["Estado", selectedRow.status],
                ["Store", selectedRow.store_name],
                ["Cuenta / integración", selectedRow.account_name || (selectedRow.account_id ? `Cuenta #${selectedRow.account_id}` : "—")],
                ["Fecha de venta", formatDate(selectedRow.fecha_venta)],
                ["Fecha de colecta", formatDate(selectedRow.fecha_colecta)],
                ["Comprador", selectedRow.nombre_destinatario],
                ["Dirección", selectedRow.direccion],
                ["CP", selectedRow.codigo_postal],
                ["Localidad", selectedRow.localidad],
                ["Chofer", selectedRow.chofer],
                ["Teléfono", selectedRow.telefono_destinatario],
                ["Email", selectedRow.email_destinatario],
                ["Precio cliente", formatCurrency(selectedRow.precio_cliente)],
                ["Precio chofer", formatCurrency(selectedRow.precio_chofer)],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "16px",
                    background: "var(--surface-alt)",
                    padding: "14px",
                  }}
                >
                  <div style={labelTextStyle}>{label}</div>
                  <div>{value || "—"}</div>
                </div>
              ))}

              <div
                style={{
                  gridColumn: "1 / -1",
                  border: "1px solid var(--border)",
                  borderRadius: "16px",
                  background: "var(--surface-alt)",
                  padding: "14px",
                }}
              >
                <div style={labelTextStyle}>Comentario destino</div>
                <div>{selectedRow.comentario_destino || "—"}</div>
              </div>

              <OpsHistoryPanel deliveryId={selectedRow.id} token={token} />
              <DeliveryMlDataPanel deliveryId={selectedRow.id} token={token} />
            </div>
          </Card>
        </div>
      ) : null}

      <style>
        {`
          .deliveries-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .deliveries-filters-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .deliveries-kpi-grid,
            .deliveries-filters-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .deliveries-kpi-grid,
            .deliveries-filters-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}